import { BrowserSupport } from '@tableau/browser-support';
import Axios, { AxiosResponse } from 'axios';
import React from 'react';
import { classes } from 'typestyle';

import { telemetryConsumerWrapper } from '~commonInfra/telemetryEvents/contexts/TelemetryConsumerWrapper';
import { ContentClickedTelemetryContext } from '~commonInfra/telemetryEvents/contexts/TelemetryContexts';
import { ContentClickedEventHandler } from '~commonInfra/telemetryEvents/types/ContentTelemetryTypes';
import { mouseUpFactory } from '~commonInfra/telemetryEvents/util/EventHandlerFactories';
import { generateUniqueId } from '~src/spacesPlatform/utils/DomUtils';
import { cssClass } from '~src/style';

import { CardViewStyles, TOTAL_CARD_WIDTH } from './CardViewStyles';

export const ThumbnailTestId = 'thumbnail';
const cssPrefix = 'Thumbnail';

export type ThumbnailProps = Pick<React.AriaAttributes, 'aria-label'> & {
  /** The url to navigate to when clicking the thumbnail; the thumbnail will have a link element only if this is present */
  readonly navigateUrl?: string;
  /** The default image url, used when `thumbnailImageUrl` is not present or fails to load */
  readonly defaultImageUrl: string;
  /** Whether the link dragging should be disabled or not */
  readonly allowDragAndDrop: boolean;
  /** Whether the `navigateUrl` should be opened in a new tab or current tab */
  readonly openLinkInNewTab?: boolean;
  /** The image url */
  readonly thumbnailImageUrl?: string;
  /** Whether or not to set `background-size: cover` when styling the thumbnail */
  readonly imageCoverBackground?: boolean;
  /** Whether the thumbnail should look like a stack of cards (useful for workbook thumbnail) or just one card */
  readonly decorate?: boolean;
  /** Whether or not to show hover styling on the thumbnail */
  readonly hovered?: boolean;
  /** Whether or not to show selection styling on the thumbnail */
  readonly selected?: boolean;
  /** Whether the `thumbnailImageUrl` should be treated as a base64 encoded svg data URI instead of an actual image url */
  readonly useBase64EncodedImage?: boolean;
  /** Height override for the thumbnail; if not present then the height defaults to 184px specified in `CardViewStyles` */
  readonly customHeight?: number;
  /** Text to be rendered at the center of thumbnail */
  readonly centeredText?: string;
  /**
   * Props for making thumbnail a custom SVG shape. For example, the project card is in the shape of 
   * a folder. If you provide `CustomShape` props, `Thumbnail` will take care of making the content of your thumbnail
   * the desired shape, and cropping the background image and hover/selection overlay to match the shape. Applies
   * special styles for the focus indicator so that it will properly follow the contours of the shape,
   * rather than being cut off. 
   * 
   * NOTE: Use `makeCustomThumbnailShape` helper to generate `CustomShape` props 
   */
  readonly customShape?: CustomShape;
}

export interface ThumbnailState {
  readonly loadThumbnailImage: boolean;
}

const CARD_STACK_DECORATION = <div className={CardViewStyles.ThumbnailWorkbookDecoration} />;

export class Thumbnail extends React.Component<ThumbnailProps, ThumbnailState> {
  private thumbnailImage: HTMLImageElement;
  private encodedSvg: string;

  constructor(props: ThumbnailProps) {
    super(props);
    this.state = { loadThumbnailImage: false };
    this.thumbnailImage = new Image();
  }

  public componentDidMount() {
    if (this.props.thumbnailImageUrl) {
      // If we are using a data uri, we need to fetch the content first
      if (this.props.useBase64EncodedImage) {
        Axios.get(this.props.thumbnailImageUrl)
          .then((result: AxiosResponse) => {
            this.encodedSvg = this.sanitizeEncodedSvg(result.data);
            this.setState({loadThumbnailImage: true});
          });
      } else {
        this.thumbnailImage.src = this.props.thumbnailImageUrl;
        this.thumbnailImage.onload = () => {
          this.setState({ loadThumbnailImage: true });
        };
      }
    }
  }

  private sanitizeEncodedSvg(encodedSvg: string): string {
    // remove xlink:href attribute
    let sanitized = atob(encodedSvg).replace(/xlink:href=\"[^\"]*\"/ig, '');
    // Remove doctype
    sanitized = sanitized.replace(/<!DOCTYPE[\S\s]*]/ig, '');
    return btoa(sanitized);
  }

  public componentWillUnmount() {
    this.thumbnailImage.onload = null;
  }

  private buildThumbnailOverlayDiv(): JSX.Element {
    return (
      <div data-tb-test-id={'thumbnail-overlay'}
        className={classes(CardViewStyles.SelectOverlay, this.props.customShape?.clippingClass)}
        style={{ height: this.props.customHeight }}>
      </div>
    );
  }

  public render() {
    if (this.props.navigateUrl && !this.props['aria-label']) {
      throw new Error(`aria-label must be provided in order to meet accessibility requirements`);
    }

    const imageUri = this.props.useBase64EncodedImage ? `data:image/svg+xml;base64,${this.encodedSvg}` : this.props.thumbnailImageUrl;
    const backgroundImageUrl = this.state.loadThumbnailImage ? imageUri : this.props.defaultImageUrl;

    const focusClass = this.props.customShape ? CustomBorderAndFocusClass : CardViewStyles.ThumbnailFocus;

    const classNames = classes(focusClass,
      !this.props.customShape && (this.state.loadThumbnailImage ? CardViewStyles.ThumbnailPreloaded : CardViewStyles.Thumbnail), {
      [CardViewStyles.CardSelectionHighlight]: this.props.selected,
      [CardViewStyles.CardHoverHighlight]: this.props.hovered && !this.props.selected,
    });

    const loadedImageOrCoverBackground = this.props.imageCoverBackground || this.state.loadThumbnailImage;

    const thumbnailStyle: React.CSSProperties = {
      backgroundImage: `url(${backgroundImageUrl})`,
      backgroundSize: loadedImageOrCoverBackground ? 'cover' : '',
      height: this.props.customHeight,
    };

    return telemetryConsumerWrapper(ContentClickedTelemetryContext.Consumer, (sendOpenContentTelemetry: ContentClickedEventHandler) => {
      return this.props.navigateUrl ?
        <div data-tb-test-id={ThumbnailTestId}>
          <a href={this.props.navigateUrl}
            target={this.props.openLinkInNewTab ? '_blank' : null}
            rel="noopener noreferrer"
            /**
             * If card reordering allowed, disable link dragging.
             * Otherwise, set `draggable` explicitly to undefined rather than true.
             * (When `draggable` is true, the browser-provided drag avatar looks too similar
             * to the reorder card feature's avatar, which may confuse users.)
             */
            draggable={this.props.allowDragAndDrop ? false : undefined}
            onMouseUp={mouseUpFactory<HTMLAnchorElement>(sendOpenContentTelemetry)}
            className={classNames}
            style={this.props.customShape ? { height: this.props.customHeight } : thumbnailStyle}
            aria-label={this.props['aria-label']}
          >
            {this.getInnerContent(thumbnailStyle)}
            {this.buildThumbnailOverlayDiv()}
          </a>
          {this.props.decorate && CARD_STACK_DECORATION}
        </div> :
        <div data-tb-test-id={ThumbnailTestId}>
          <div
            className={classNames}
            style={this.props.customShape ? { height: this.props.customHeight } : thumbnailStyle}
          >
            {this.getInnerContent(thumbnailStyle)}
            {this.buildThumbnailOverlayDiv()}
          </div>
          {this.props.decorate && CARD_STACK_DECORATION}
        </div>;
    });
  }

  private getInnerContent = (thumbnailStyle: React.CSSProperties) => {
    /**
     * This may look silly, but it is purposeful. We are passing in both the SVG for the actual card AND
     * we are providing a clip path of the same shape. The clip path is used to clip the background image 
     * and the overlay shown on hover/selection to match the custom shape here (e.g. the folder shape for 
     * projects). The background image is purposefully a separate div and not just the background of the 
     * SVG because it was not performant enough as the background of the SVG (and not supported).
     */
    if (this.props.customShape) {
      return <>
        {this.props.customShape.customShape}
        {this.props.customShape.clipPath}
        <div style={thumbnailStyle} className={classes(this.props.customShape.clippingClass, CustomShapeBackgroundImageClass)} />
      </>;
    }

    return <>
      {this.props.centeredText && <div className={CardViewStyles.ThumbnailCenteredText}>{this.props.centeredText}</div>}
    </>;
  };
}

// ----------- Custom Shape Helpers and Styles -----------
const CustomShapeBackgroundImageClass = cssClass(cssPrefix, 'shape-background-image', {
  width: TOTAL_CARD_WIDTH,
  position: 'absolute',
  top: 0,
  zIndex: -1,
});

/**
 * This applies styles to the SVG that would usually be in `border` 
 * and `box-shadow` properties. Have to apply `drop-shadow` filter
 * to parent element for halo effect and stroke for `border`.
 */
const CustomBorderAndFocusClass = cssClass(cssPrefix, 'border-and-focus', {
  filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,.15))',
  display: 'block',
  $nest: {
    '&:focus-visible': {
      filter: 'drop-shadow(0px 0px 4px rgb(11, 92, 171))'
    },
    // svg stylings
    '& svg': {
      fill: 'none',
      stroke: '#d8d8d8',
    },
    '&:focus-visible svg': {
      stroke: 'rgb(11, 92, 171)'
    },
  }
});

export interface CustomShape {
  /** SVG for card */
  customShape: React.ReactElement;
  /** Clip path SVG so that background image and overlay can be clipped */
  clipPath: React.ReactElement;
  /** ID for `clipPath` def */
  clipPathId: string;
  /** Class applying `clipPath` styles */
  clippingClass: string;
}

export function makeCustomThumbnailShape(drawPath: string, clipPathId: string = generateUniqueId('clip-path')): CustomShape {
  const shapeSVG = <svg style={{ width: TOTAL_CARD_WIDTH, height: 'inherit' }}><path d={drawPath} /></svg>;
  const clipPathSVG = <svg style={{ width: 0, height: 0 }}>
    <defs>
      <clipPath id={clipPathId}>
        <path d={drawPath} />
      </clipPath>
    </defs>
  </svg>;

  /**
   * These clipping styles will be applied to the background image and the overlay shown on hover/selection,
   * so it can match the custom shape passed via the `customShape` props.
   */
  const clippingClass = cssClass('Thumbnail', clipPathId, {
    clipPath: `url(#${clipPathId})`,
    // styles below added for Safari, without this does not properly clip shapes after hover or on scroll
    // https://stackoverflow.com/questions/41860477/why-doesnt-css-clip-path-with-svg-work-in-safari
    ...(BrowserSupport.IsSafari() && {
      '-webkit-clip-path': `url(#${clipPathId})`,
      transform: 'translateZ(0)',
    }),
  });

  return ({
    clipPath: clipPathSVG,
    clipPathId,
    clippingClass,
    customShape: shapeSVG,
  });
}